/* Bandeja de triaje — la puerta del producto (pantalla 2a del rediseño).
 *
 * El trabajo que sostiene: alguien tiene veinte minutos y ~1.800 convocatorias abiertas. La bandeja
 * existe para descartar rápido, no para leer. Por eso la fila es una LÍNEA con lo justo para
 * decidir mirar —afinidad, objeto, plata, cierre— y el porqué vive al lado, en un panel que cambia
 * con el clic sin recargar la lista ni perder el sitio.
 *
 * Nada de esto se calcula aquí: la afinidad, la banda, los criterios y las tres cifras que deciden
 * las manda el servidor. El frontend ordena píxeles.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { enviar, ErrorApi, obtener, reemplazar } from '../api/cliente'
import {
  diasHasta, entero, normalizarNombre, normalizarObjeto, pesos, plazo, plazoEsCuenta,
} from '../api/formato'
import type {
  Cartera, Criterio, Meta, Proceso, RespuestaBusqueda, Veredicto,
} from '../api/tipos'
import { MOTIVOS_DESCARTE, type ResultadoOferta } from '../api/tipos'
import { usePerfil } from '../estado/perfil'
import { Banda, BarraAfinidad } from '../ui/Banda'
import { QuePaso } from '../ui/QuePaso'
import { IconoAviso, IconoNo, IconoQuitar, IconoSi, IconoSinDato } from '../ui/Iconos'
import { usarAngosto } from '../ui/usarAngosto'
import './Bandeja.css'

type Lista = 'activos' | 'sigo' | 'desc'
const LOTE = 24

/**
 * Los tramos de presupuesto del filtro.
 *
 * El primer renglón se llama «Presupuesto» a secas —igual que «Sector» y «Cierra en»— porque es el
 * estado SIN filtrar, y en el 2a esos tres controles se dibujan con el nombre del filtro. Escribir
 * «Presupuesto: cualquiera» decía lo mismo con el doble de ancho, y con cinco controles en un solo
 * renglón el ancho es lo que decide si la barra cabe o se parte en dos.
 *
 * ⚠ Todos llevan `min` a partir de 1 peso, incluido el primero. En esta fuente un presupuesto en 0
 * significa «no diligenciado», no «cuesta cero», y el filtro del servidor lee `precio_base or 0`:
 * sin el mínimo, «hasta $100 millones» arrastraría todos los procesos SIN presupuesto publicado y
 * afirmaría de ellos algo que nadie sabe. No se puede filtrar por una cifra que no existe, así que
 * elegir un tramo excluye lo que no la tiene — y por eso el primer renglón dice «cualquiera», que
 * es el único que sí los incluye.
 */
/** Tipos de proceso que la bandeja sabe filtrar. Son los grupos de `app/modalidad.py`,
 *  no las 13 etiquetas literales de SECOP: «Licitación pública» y «… Obra Pública» son
 *  el mismo tipo. `otra` cubre RFI, régimen especial y directa con ofertas. */
const TIPOS: { clave: string; etiqueta: string }[] = [
  { clave: '', etiqueta: 'Tipo' },
  { clave: 'licitacion', etiqueta: 'Licitación' },
  { clave: 'abreviada', etiqueta: 'Menor cuantía' },
  { clave: 'subasta', etiqueta: 'Subasta inversa' },
  { clave: 'minima', etiqueta: 'Mínima cuantía' },
  { clave: 'concurso', etiqueta: 'Concurso de méritos' },
  { clave: 'otra', etiqueta: 'Otras' },
]

const TRAMOS: { clave: string; etiqueta: string; min?: number; max?: number }[] = [
  { clave: '', etiqueta: 'Presupuesto' },
  { clave: 'a', etiqueta: 'Hasta $100 millones', min: 1, max: 100e6 },
  { clave: 'b', etiqueta: '$100 a $1.000 millones', min: 100e6, max: 1_000e6 },
  { clave: 'c', etiqueta: 'Más de $1.000 millones', min: 1_000e6 },
]

export function Bandeja() {
  const { perfil, rup, marcar, marcarEn, registrarResultado, recargar: recargarPerfil } = usePerfil()
  const navegar = useNavigate()
  /* Por debajo de 640 px la decisión se abre dentro de la fila en vez de vivir en la columna de la
     derecha. Es un cambio de SITIO en el documento, no de aspecto, y por eso no lo puede hacer la
     CSS sola — el porqué está en `usarAngosto`. */
  const angosto = usarAngosto()

  const [meta, setMeta] = useState<Meta | null>(null)
  const [datos, setDatos] = useState<RespuestaBusqueda | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [cargando, setCargando] = useState(true)

  const [lista, setLista] = useState<Lista>('activos')
  const [texto, setTexto] = useState('')
  const [sector, setSector] = useState('')
  const [tipo, setTipo] = useState('')
  // Los otros dos filtros del documento 2a (Sector · Cierra en · Presupuesto). El servidor ya
  // aceptaba `dias_max`, `valor_min` y `valor_max`; lo que faltaba era la barra.
  const [plazoF, setPlazoF] = useState('')
  const [plataF, setPlataF] = useState('')
  const [orden, setOrden] = useState<'rel' | 'cierre' | 'plata'>('rel')
  const [soloNuevos, setSoloNuevos] = useState(false)
  const [soloUnspsc, setSoloUnspsc] = useState(false)
  const [limite, setLimite] = useState(LOTE)

  const [selId, setSelId] = useState<string | null>(null)
  /* La cartera, para el selector «Trabajando con». Solo se pinta con DOS o más empresas: con una,
     un selector de un elemento es ruido que además insinúa que falta algo. */
  const [cartera, setCartera] = useState<Cartera | null>(null)
  const activaId = cartera?.activa_id ?? null
  /* Modo «todas» del 5b: puntuar cada proceso contra TODA la cartera y quedarse con la mejor. Es
     para triar la mañana de un consultor con varias empresas en un solo escaneo. */
  const [todas, setTodas] = useState(false)
  useEffect(() => {
    obtener<Cartera>('/api/cartera').then(setCartera).catch(() => setCartera(null))
  }, [])

  useEffect(() => {
    obtener<Meta>('/api/oportunidades/meta').then(setMeta).catch(() => setMeta(null))
  }, [])

  // El texto no dispara una búsqueda por tecla: se espera a que la persona pare de escribir.
  const [textoAplicado, setTextoAplicado] = useState('')
  useEffect(() => {
    const t = window.setTimeout(() => setTextoAplicado(texto), 350)
    return () => window.clearTimeout(t)
  }, [texto])

  useEffect(() => {
    const corte = new AbortController()
    setCargando(true)
    // Sin `perfil`, `rup` ni `watch` en el cuerpo: los pone el servidor desde la cuenta.
    const tramo = TRAMOS.find((t) => t.clave === plataF)
    enviar<RespuestaBusqueda>(
      '/api/oportunidades/buscar',
      {
        lista, texto: textoAplicado, sector, modalidad: tipo, orden, solo_nuevos: soloNuevos,
        solo_unspsc: soloUnspsc, limit: limite,
        /* La empresa va EXPLÍCITA aunque el servidor sepa cuál es la activa de la sesión. Dos
           razones, y las dos mordieron o iban a morder:
             · sin ella, `activaId` no estaría en las dependencias del efecto y cambiar de empresa
               NO recargaba la lista — la píldora decía una y la lista seguía puntuada con la otra,
               en silencio. Pasó, y se vio en pantalla;
             · y quita la carrera de leer-después-de-escribir: no hay que confiar en que el PUT de
               la activa ya haya aterrizado cuando sale esta petición. */
        empresa_id: activaId,
        todas,
        // `null` y no ausente: el servidor distingue «no lo mando» de «sin tope», y aquí lo que se
        // dice es que no hay tope.
        dias_max: plazoF ? Number(plazoF) : null,
        valor_min: tramo?.min ?? null,
        valor_max: tramo?.max ?? null,
      },
      corte.signal,
    )
      .then((r) => {
        setDatos(r)
        setError(null)
      })
      .catch((e: unknown) => {
        if (corte.signal.aborted) return
        setError(e instanceof ErrorApi ? e.message : String(e))
      })
      .finally(() => {
        if (!corte.signal.aborted) setCargando(false)
      })
    return () => corte.abort()
    // ⚠ `activaId` es dependencia: es lo que hace que cambiar de empresa recargue la lista.
  }, [lista, textoAplicado, sector, tipo, plazoF, plataF, orden, soloNuevos, soloUnspsc, limite, activaId, todas])

  const procesos = useMemo(() => datos?.procesos ?? [], [datos])

  // Si la selección desaparece del resultado (cambió el filtro, se descartó), se toma la primera:
  // un panel de decisión vacío al lado de una lista llena parece un error de la aplicación.
  useEffect(() => {
    if (!procesos.length) {
      setSelId(null)
      return
    }
    /* En angosto la lista arranca CERRADA, como la dibuja el documento: ahí la decisión se abre
       dentro de la fila y empuja el resto, así que preseleccionar la primera dejaría el primer
       pantallazo ocupado por un panel que nadie pidió, con la lista debajo. En escritorio sí se
       preselecciona: el panel vive en su columna y vacío al lado de una lista llena parece un
       error de la aplicación. */
    if (angosto) {
      if (selId && !procesos.some((p) => p.id === selId)) setSelId(null)
      return
    }
    if (!selId || !procesos.some((p) => p.id === selId)) setSelId(procesos[0]!.id)
  }, [procesos, selId, angosto])

  /* Confirmación de la última marca, NOMBRANDO la empresa. En modo cartera la fila puede ser de
     cualquiera de la cartera, y una marca que no dice a dónde fue es una marca en la que no se
     puede confiar. */
  const [confirmacion, setConfirmacion] = useState<string | null>(null)

  /**
   * Marcar respetando a QUIÉN pertenece la fila.
   *
   * En modo cartera cada fila es de una empresa distinta (`_para`), y descartar una que salió «para
   * Maderas» tiene que escribir en el watch de Maderas. Va por el direccionamiento explícito por
   * `empresa_id` — el mismo que ya protege el resto de las escrituras—, no por el perfil activo.
   */
  const marcarFila = useCallback((
    id: string, estado: 'sigo' | 'desc' | null, motivo?: string,
  ) => {
    const p = procesos.find((x) => x.id === id)
    const para = p?._para
    const verbo = estado === 'sigo' ? 'Siguiendo' : estado === 'desc' ? 'Descartado' : 'Sin marca'
    if (!para || para.empresa_id === activaId) {
      marcar(id, estado, motivo)
      setConfirmacion(estado ? verbo : null)
      return
    }
    const nombre = normalizarNombre(para.razon_social) || 'esa empresa'
    setConfirmacion(`${verbo} para ${nombre}…`)
    void marcarEn(para.empresa_id, id, estado, motivo)
      .then(() => setConfirmacion(estado ? `${verbo} para ${nombre}` : `Sin marca en ${nombre}`))
      .catch(() => setConfirmacion(`No se pudo marcar en ${nombre}. Recarga y vuelve a intentar.`))
  }, [procesos, activaId, marcar, marcarEn])

  const cambiarEmpresa = useCallback(async (id: number) => {
    await reemplazar('/api/cartera/activa', { empresa_id: id })
    // El perfil en memoria es el de la empresa ANTERIOR. Sin recargarlo, la lista se reordenaría
    // con el perfil nuevo mientras las marcas de seguido y descartado seguirían siendo del viejo.
    await recargarPerfil()
    // Y esto es lo que dispara la recarga de la lista: `activa_id` cambia, y va en las
    // dependencias del efecto de búsqueda.
    setCartera(await obtener<Cartera>('/api/cartera'))
    setSelId(null)
  }, [recargarPerfil])

  const conteos = useMemo(() => {
    const marcas = Object.values(perfil.watch ?? {})
    return {
      sigo: marcas.filter((m) => m.estado === 'sigo').length,
      desc: marcas.filter((m) => m.estado === 'desc').length,
      /* Del SERVIDOR, no de `procesos`. Contarlo aquí era contar sobre el lote cargado —24 de 68—
         y enseñarlo como si fuera del total: un número siempre a la baja y sin nada que avisara.
         `nuevos` sí puede quedarse en el cliente: es una marca de la fila, no un agregado. */
      cierran: datos?.cierran_pronto,
      nuevos: procesos.filter((p) => p.nuevo).length,
    }
  }, [perfil.watch, procesos, datos?.cierran_pronto])

  /**
   * El sitio de un proceso dentro del resultado, para que la ficha pueda decir «2 de 68» y ofrecer
   * el siguiente.
   *
   * Viaja por el estado de navegación del router y NO por la URL ni por el servidor: es contexto de
   * ESTA lista —con estos filtros y este orden—, no una propiedad del proceso. Quien llega a la
   * ficha por un enlace compartido no tiene lista de la que formar parte, y allí la barra se pinta
   * sin contador y sin «siguiente», que es lo honesto: inventar un «2 de 68» para alguien que no
   * hizo esa búsqueda sería decirle algo que no es suyo.
   */
  const sitio = useMemo(() => ({
    ids: procesos.map((p) => p.id),
    total: datos?.total ?? procesos.length,
  }), [procesos, datos?.total])

  /**
   * Cerrar el acordeón devolviendo el foco a la fila que lo abrió.
   *
   * El scroll vuelve solo —al salir el panel del flujo, la fila recupera su sitio—, pero el foco no:
   * al desmontarse el botón de cerrar, se cae a `<body>` y quien navega con teclado se queda sin
   * punto de partida, en una lista de sesenta y ocho elementos. Se devuelve al `<button>` de la
   * fila, que es de donde salió.
   */
  const cerrarFila = useCallback((id: string | null) => {
    setSelId(null)
    if (!id) return
    // Tras el repintado: el elemento tiene que existir para poder enfocarlo.
    requestAnimationFrame(() => {
      document.querySelector<HTMLButtonElement>(`[data-fila="${CSS.escape(id)}"]`)?.focus()
    })
  }, [])

  /* El panel de decisión, pintado en UN solo sitio: la columna de la derecha en escritorio, o
     dentro de la fila abierta en angosto. Una sola función para los dos sitios porque son el mismo
     panel con los mismos datos — si fueran dos llamadas distintas, cada arreglo habría que hacerlo
     dos veces y una de las dos se quedaría atrás. */
  const panelDe = useCallback((id: string | null) => {
    const p = procesos.find((x) => x.id === id)
    return (
      <PanelDecision
        procesoId={id}
        proceso={p}
        entidad={p ? datos?.entidades?.[p.entidad_nit] : undefined}
        onMarcar={marcarFila}
        onResultado={registrarResultado}
        marca={id ? (perfil.watch?.[id]?.estado ?? null) : null}
        resultado={id ? perfil.watch?.[id]?.resultado : undefined}
        onCerrar={angosto ? () => cerrarFila(id) : undefined}
        sitio={sitio}
      />
    )
  }, [procesos, datos?.entidades, marcarFila, registrarResultado, perfil.watch, angosto, cerrarFila, sitio])

  // Es del PERFIL, no del proceso: o el servidor siembra `_afinidad` en todas las filas o en
  // ninguna. Se mira la primera y basta.
  const sinAfinidad = Boolean(procesos.length) && procesos.every((p) => p._afinidad == null)

  /* Los atajos que el diseño rotula en el pie de la lista: J/K para moverse, S seguir, X descartar,
   * Enter abrir la ficha. Es una bandeja de TRIAJE —alguien con veinte minutos y 1.800
   * convocatorias—, y sin teclado cada descarte cuesta apuntar y hacer clic.
   *
   * ⚠ No se disparan mientras se escribe: el manejador va en `window` y sin esta guarda la «s» del
   * buscador seguiría un proceso y la «x» lo descartaría. */
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      const foco = document.activeElement
      const escribiendo = foco instanceof HTMLElement
        && (['INPUT', 'TEXTAREA', 'SELECT'].includes(foco.tagName) || foco.isContentEditable)
      if (escribiendo || e.metaKey || e.ctrlKey || e.altKey || !procesos.length) return

      const i = procesos.findIndex((p) => p.id === selId)
      const mover = (paso: number) => {
        const siguiente = procesos[Math.max(0, Math.min(procesos.length - 1, i + paso))]
        if (siguiente) setSelId(siguiente.id)
      }
      const actual = procesos[i]
      switch (e.key.toLowerCase()) {
        case 'j': e.preventDefault(); mover(1); break
        case 'k': e.preventDefault(); mover(-1); break
        case 's':
          if (!actual) return
          e.preventDefault()
          marcarFila(actual.id, perfil.watch?.[actual.id]?.estado === 'sigo' ? null : 'sigo')
          break
        case 'x':
          if (!actual) return
          e.preventDefault()
          // Con teclado el motivo no se pregunta: interrumpir el triaje con un diálogo lo anula.
          // «No me interesa» es el motivo por omisión, el mismo del botón sin elegir motivo.
          marcarFila(actual.id, perfil.watch?.[actual.id]?.estado === 'desc' ? null : 'desc',
                 perfil.watch?.[actual.id]?.estado === 'desc' ? undefined : 'No me interesa')
          break
        case 'enter':
          if (!actual) return
          e.preventDefault()
          navegar(`/proceso/${actual.id}`, { state: sitio })
          break
        default:
      }
    }
    window.addEventListener('keydown', alPulsar)
    return () => window.removeEventListener('keydown', alPulsar)
  }, [procesos, selId, marcarFila, navegar, perfil.watch])

  return (
    <div className="bandeja">
      <nav className="conteos" aria-label="Vistas">
        <Conteo activo={lista === 'activos'} onClick={() => setLista('activos')}
                etiqueta="Para mí" n={datos?.total} />
        {/* Filtra, no informa. Era el único conteo sin `onClick` —un número de adorno al lado de
            cuatro que sí filtran—, y el filtro que necesitaba ya existía: `dias_max = 7`, la misma
            ventana con la que el servidor cuenta la cifra, así que el chip y su número no se
            pueden contradecir. Vuelve a pulsarse para quitarlo. */}
        <Conteo etiqueta="Cierran esta semana" n={conteos.cierran}
                activo={plazoF === '7'}
                onClick={() => setPlazoF((v) => (v === '7' ? '' : '7'))} />
        <Conteo etiqueta="Nuevos" n={conteos.nuevos} activo={soloNuevos}
                onClick={() => setSoloNuevos((v) => !v)} />
        {/* Apagado por omisión: el filtro duro por RUP esconde 22-40 % de lo que la empresa
            SÍ ha ganado. El title y la nota debajo dicen el costo, no solo el beneficio. */}
        {((rup?.unspsc?.length ?? perfil.unspsc?.length ?? 0) > 0) && (
          <Conteo etiqueta="Solo tu RUP" activo={soloUnspsc}
                  titulo="Solo procesos de tu clasificación RUP. Deja fuera entre 2 y 4 de cada 10 que sí has ganado."
                  onClick={() => setSoloUnspsc((v) => !v)} />
        )}
        <Conteo activo={lista === 'sigo'} onClick={() => setLista('sigo')}
                etiqueta="Siguiendo" n={conteos.sigo} />
        <Conteo activo={lista === 'desc'} onClick={() => setLista('desc')}
                etiqueta="Descartados" n={conteos.desc} />
        {/* El hueco de «Vistas guardadas» del diseño. Se deja dicho aunque las vistas lleguen con
            la cartera: es donde van, y un hueco declarado se lee mejor que un hueco a secas. */}
        {/* Cuántos esconde el PROPIO PERFIL. No es un filtro de la barra —esos se ven puestos—
            sino las reglas que alguien configuró otro día en otra pantalla, y sin esta cifra
            esconden procesos sin dejar rastro. Decirlo es lo que las hace reversibles. */}
        {Boolean(datos?.bloqueados_por_regla) && (
          <Link className="conteo" to="/perfil" title="Tus reglas de «lo que no quieres ver»">
            Bloqueadas por tu perfil · <b>{entero(datos?.bloqueados_por_regla ?? null)}</b>
          </Link>
        )}
        {/* El hueco de «Vistas guardadas» del diseño. Se deja dicho aunque las vistas lleguen con
            la cartera: es donde van, y un hueco declarado se lee mejor que un hueco a secas. */}
        <span className="conteos-extra">Vistas guardadas · próximamente</span>
      </nav>

      {/* «Trabajando con», del 5b. Cambiar de empresa aquí es cambiar LA ACTIVA de esta sesión, no
          un filtro de la lista: la afinidad, el seguimiento y los descartes son por empresa, así
          que media pantalla cambia de significado. Por eso lleva a `PUT /api/cartera/activa` y
          recarga el perfil — y por eso otra pestaña del mismo navegador NO se entera hasta que
          recargue, que es lo que se quiere. */}
      {(cartera?.empresas.length ?? 0) > 1 && (
        <div className="trabajando">
          <span className="rotulo">Trabajando con</span>
          {/* «Todas» primero: es el modo de triaje, y quien lleva varias empresas empieza por ahí.
              No cambia la empresa activa —solo cómo se puntúa esta lista— así que no toca nada de
              lo que vean las otras pestañas. */}
          <button
            type="button"
            className={`chip-empresa${todas ? ' activa' : ''}`}
            aria-pressed={todas}
            onClick={() => setTodas(true)}
            disabled={todas}
          >
            Todas · {entero(cartera?.empresas.length ?? null)} empresas
          </button>
          {cartera?.empresas.map((e) => (
            <button
              type="button"
              key={e.id}
              className={`chip-empresa${!todas && e.id === cartera.activa_id ? ' activa' : ''}`}
              aria-pressed={!todas && e.id === cartera.activa_id}
              disabled={!todas && e.id === cartera.activa_id}
              title={e.razon_social || `NIT ${e.nit}`}
              onClick={() => { setTodas(false); void cambiarEmpresa(e.id) }}
            >
              {normalizarNombre(e.razon_social) || `NIT ${e.nit}` || 'Sin nombre'}
            </button>
          ))}
          <Link to="/empresas" className="trabajando-todas">Tus empresas</Link>
        </div>
      )}

      {/* Si el tope dejó empresas fuera, se DICE. Un tope silencioso que quita empresas del triaje
          de la mañana es peor que no tener el modo. */}
      {datos?.cartera && datos.cartera.puntuadas < datos.cartera.pedidas && (
        <p className="trabajando-aviso">
          Se están comparando <b>{entero(datos.cartera.puntuadas)} de tus{' '}
          {entero(datos.cartera.pedidas)}</b> empresas con perfil — el máximo por búsqueda.{' '}
          <Link to="/empresas">Elige cuáles entran</Link> o mira una por una.
        </p>
      )}

      <div className="tabla-y-panel">
        <section className={`tabla${sinAfinidad ? ' sin-afinidad' : ''}`} aria-label="Procesos">
          <div className="filtros">
            {/* Buscador + tipo en un renglón: el tipo en la fila de abajo empujaba
                presupuesto y orden y se partía toda la barra. El buscador se queda
                con el resto del ancho. */}
            <div className="filtros-buscar">
              <input
                className="buscar"
                type="search"
                placeholder="Buscar…"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                aria-label="Buscar procesos"
              />
              <select className="f-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}
                      aria-label="Tipo">
                {TIPOS.map((t) => (
                  <option key={t.clave} value={t.clave}>{t.etiqueta}</option>
                ))}
              </select>
            </div>
            <div className="filtros-selects">
              <select value={sector} onChange={(e) => setSector(e.target.value)} aria-label="Sector">
                <option value="">Sector</option>
                {(meta?.sectores ?? []).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select value={plazoF} onChange={(e) => setPlazoF(e.target.value)}
                      aria-label="Cierra en">
                <option value="">Cierra en</option>
                <option value="3">Cierra en 3 días</option>
                <option value="7">Cierra esta semana</option>
                <option value="15">Cierra en 15 días</option>
                <option value="30">Cierra en 30 días</option>
              </select>
              <select className="f-plata" value={plataF} onChange={(e) => setPlataF(e.target.value)}
                      aria-label="Presupuesto">
                {TRAMOS.map((t) => (
                  <option key={t.clave} value={t.clave}>{t.etiqueta}</option>
                ))}
              </select>
              <select className="orden" value={orden}
                      onChange={(e) => setOrden(e.target.value as typeof orden)}
                      aria-label="Orden">
                <option value="rel">Orden: revisar primero</option>
                <option value="cierre">Orden: cierra antes</option>
                <option value="plata">Orden: presupuesto</option>
              </select>
            </div>
          </div>
          {soloUnspsc && (
            <p className="sin-afinidad-aviso">
              Solo procesos de tu clasificación RUP. Deja fuera entre 2 y 4 de cada 10 que sí
              has ganado — por eso no viene puesto.
            </p>
          )}

          {/* Sin perfil, la columna «Afinidad» imprime un punto en cada fila y no dice por qué. Un
              punto no es un dato ni un error: es un hueco, y quien lo mira no puede saber si la
              página está rota, cargando, o esperando algo de él. La razón tiene que estar DONDE
              falta el número, no solo en la píldora del masthead ni en el panel de la derecha,
              que hay que abrir para verlo. */}
          {sinAfinidad && (
            <p className="sin-afinidad-aviso">
              <b>Todavía no podemos calcular la afinidad.</b> Compara cada proceso con lo que tu
              empresa ya ejecutó en SECOP y con tu RUP, y no tenemos ninguna de las dos cosas.{' '}
              <Link to="/perfil">Carga tu RUP o escribe tu NIT</Link> y esta lista se reordena por
              lo que le sirve a tu empresa. Mientras tanto va ordenada por criterios generales.
            </p>
          )}

          <div className={`encabezado${todas ? ' con-para' : ''}`} role="presentation">
            <span>{sinAfinidad ? '' : 'Afinidad'}</span>
            {todas && <span>Para</span>}
            <span>Proceso</span>
            <span className="der">Presupuesto</span>
            <span className="der">Cierra</span>
          </div>

          {error && <p className="aviso-error" role="alert">{error}</p>}
          {!error && cargando && !datos && <p className="rotulo">Cargando</p>}
          {!error && datos && !procesos.length && (
            <p className="vacio">
              {lista === 'desc'
                ? 'No has descartado nada todavía.'
                : lista === 'sigo'
                  ? 'No estás siguiendo ningún proceso.'
                  : 'Ningún proceso abierto coincide con lo que buscas.'}
            </p>
          )}

          <ul className="filas">
            {procesos.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={`fila${p.id === selId ? ' activa' : ''}${todas ? ' con-para' : ''}`}
                  aria-current={p.id === selId}
                  data-fila={p.id}
                  /* En angosto la fila ES el control que despliega la decisión, así que lo dice.
                     En escritorio no: ahí no despliega nada, solo cambia lo que muestra la columna
                     de al lado, y anunciar «expandido» sería describir algo que no ocurre. */
                  aria-expanded={angosto ? p.id === selId : undefined}
                  onClick={() => setSelId(p.id)}
                  onDoubleClick={() => navegar(`/proceso/${p.id}`, { state: sitio })}
                >
                  <span className={`afinidad cifra${p._banda ? ` b-${p._banda}` : ''}`}
                        aria-hidden={p._afinidad == null}>
                    {p._afinidad ?? ''}
                  </span>
                  {/* PARA QUIÉN. En modo cartera es la primera pregunta de cada fila: la misma
                      lista lleva procesos de empresas distintas, y sin esto el 84 no dice de quién
                      es. Va antes del objeto porque cambia cómo se lee todo lo demás. */}
                  {todas && (
                    <span className="para" title={p._para?.razon_social || ''}>
                      {normalizarNombre(p._para?.razon_social ?? '') || '—'}
                    </span>
                  )}
                  <span className="objeto">
                    <span className="objeto-txt" title={p.descripcion || p.objeto}>
                      {normalizarObjeto(p.descripcion || p.objeto,
                                        [datos?.entidades?.[p.entidad_nit]?.ciudad,
                                         datos?.entidades?.[p.entidad_nit]?.departamento])}
                    </span>
                    <span className="objeto-sub">
                      {/* El nombre se compone en caja de nombre porque en SECOP viene gritado
                          («ALCALDIA DE CHOCONTA») y el diseño lo escribe «Alcaldía de Rionegro».
                          El original va en el `title`: aquí solo cambia la caja, y las tildes que
                          la fuente no trae no se inventan. */}
                      <span title={datos?.entidades?.[p.entidad_nit]?.nombre ?? p.entidad_nit}>
                        {normalizarNombre(datos?.entidades?.[p.entidad_nit]?.nombre) || p.entidad_nit}
                      </span>
                      {/* En un envoltorio propio SOLO para poder ocultarla en móvil: ahí la línea
                          lleva entidad · presupuesto · cierre, y la modalidad la desborda a dos
                          renglones. En escritorio no cambia nada. */}
                      <span className="modalidad">{' · '}{p.modalidad}</span>
                      {p.nuevo && <em className="marca-fila">nuevo</em>}
                      {perfil.watch?.[p.id]?.estado === 'sigo' && <em className="marca-fila">siguiendo</em>}
                      {perfil.watch?.[p.id]?.estado === 'desc' && <em className="marca-fila">descartado</em>}
                    </span>
                  </span>
                  <span className="plata der cifra">{pesos(p.precio_base)}</span>
                  {/* `con-verbo` habilita el «cierra en» que el móvil pone delante. Va por el CASO
                      y no siempre: `plazo()` ya trae su propio verbo en los bordes, y prefijar sin
                      mirar producía «cierra en Cierra hoy». */}
                  <span className={`cierra der cifra${(diasHasta(p.fecha_cierre) ?? 99) <= 3 ? ' urgente' : ''}${plazoEsCuenta(p.fecha_cierre) ? ' con-verbo' : ''}`}>
                    {plazo(p.fecha_cierre)}
                  </span>
                </button>
                {/* La decisión, DENTRO de la fila y empujando el resto: es lo que pide el
                    documento de móvil, y es la razón de que el panel no se pinte siempre en el
                    mismo sitio. «El panel de decisión no se vuelve modal.» */}
                {angosto && p.id === selId && panelDe(selId)}
              </li>
            ))}
          </ul>

          <div className="pie-tabla">
            {datos?.has_more ? (
              <>
                <span>{entero(datos.total - procesos.length)} más abajo</span>
                <button type="button" className="cargar" onClick={() => setLimite((n) => n + LOTE)}>
                  cargar {LOTE}
                </button>
              </>
            ) : (
              <span>{procesos.length ? 'No hay más procesos que mostrar.' : ''}</span>
            )}
            {/* El diseño los ROTULA, así que son contrato: si están escritos, funcionan. */}
            {confirmacion && (
              <span className="confirmacion" role="status">{confirmacion}</span>
            )}
            <span className="atajos">
              <kbd>J</kbd> / <kbd>K</kbd> mover · <kbd>S</kbd> seguir · <kbd>X</kbd> descartar ·{' '}
              <kbd>Enter</kbd> abrir la ficha
            </span>
          </div>
        </section>

        {/* En escritorio el panel es la columna de la derecha; en angosto se abre dentro de la fila
            (ver `panelDe` y `usarAngosto`). Aquí solo se pinta cuando NO es angosto. */}
        {!angosto && panelDe(selId)}
      </div>
    </div>
  )
}

function Conteo({ etiqueta, n, activo, onClick, titulo }: {
  etiqueta: string
  n?: number | undefined
  activo?: boolean
  onClick?: () => void
  titulo?: string
}) {
  // Punto medio entre el nombre y la cifra, como el 2a: «Para mí · 68». Sin él, «Para mí 68» se
  // lee un instante como si el 68 fuera parte del nombre.
  const contenido = (
    <>
      {etiqueta}
      {n !== undefined && <> · <b className="cifra">{entero(n)}</b></>}
    </>
  )
  if (!onClick) return <span className="conteo" title={titulo}>{contenido}</span>
  return (
    <button type="button" className={`conteo${activo ? ' activo' : ''}`} aria-pressed={activo}
            title={titulo} onClick={onClick}>
      {contenido}
    </button>
  )
}

/**
 * El porqué del proceso seleccionado. Se pide al servidor por clic, no viene con la lista: son
 * criterios y tres cifras con su procedencia, y traerlos para 24 filas de las que se mira una es
 * pagar veinticuatro veces lo mismo.
 */
function PanelDecision({ procesoId, proceso, entidad, marca, resultado, onMarcar, onResultado, onCerrar, sitio }: {
  procesoId: string | null
  /** Solo en angosto: cierra el acordeón. En escritorio el panel es una columna y no se cierra. */
  onCerrar?: (() => void) | undefined
  /** El recorrido del que forma parte, para que la ficha pueda seguir avanzando por él. */
  sitio?: { ids: string[]; total: number } | undefined
  /** El proceso seleccionado. La cabecera del 2a lo NOMBRA: sin el objeto delante, el veredicto
   *  flota — «84 Compite» no dice de qué, y basta desplazar la lista para perder la referencia. */
  proceso?: Proceso | undefined
  entidad?: { nombre: string } | undefined
  marca: 'sigo' | 'desc' | null
  resultado?: ResultadoOferta
  onMarcar: (id: string, estado: 'sigo' | 'desc' | null, motivo?: string) => void
  onResultado: (id: string, r: ResultadoOferta | null) => void
}) {
  const navegar = useNavigate()
  const [v, setV] = useState<Veredicto | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pidiendoMotivo, setPidiendoMotivo] = useState(false)
  const ultimo = useRef<string | null>(null)

  useEffect(() => {
    setPidiendoMotivo(false)
    if (!procesoId) {
      setV(null)
      return
    }
    const corte = new AbortController()
    ultimo.current = procesoId
    obtener<Veredicto>(`/api/oportunidades/${procesoId}/veredicto`, corte.signal)
      .then((r) => {
        // Llegó tarde y ya hay otra fila seleccionada: se descarta en vez de pintar el veredicto
        // de un proceso que la persona ya dejó atrás.
        if (ultimo.current === r.id) {
          setV(r)
          setError(null)
        }
      })
      .catch((e: unknown) => {
        if (corte.signal.aborted) return
        setError(e instanceof ErrorApi ? e.message : String(e))
      })
    return () => corte.abort()
  }, [procesoId])

  const alternar = useCallback(
    (estado: 'sigo' | 'desc') => {
      if (!procesoId) return
      if (marca === estado) {
        onMarcar(procesoId, null)
        return
      }
      if (estado === 'desc') {
        setPidiendoMotivo(true)
        return
      }
      onMarcar(procesoId, 'sigo')
    },
    [procesoId, marca, onMarcar],
  )

  if (!procesoId) {
    return (
      <aside className="panel">
        <div className="panel-contenido">
          <p className="vacio">Toca un proceso para ver por qué te sirve o por qué no.</p>
        </div>
      </aside>
    )
  }

  return (
    <aside className="panel" aria-live="polite">
      {/* Aquí hubo un carril pegajoso que nunca lo fue: `.bandeja` recorta con `overflow: hidden`
          para redondear sus esquinas y eso anula el `sticky` de dentro. Lo que producía era un
          recuadro de alto fijo con las acciones recortadas fuera de la ventana. Ver `Bandeja.css`. */}
      <div className="panel-contenido">
      {error && <p className="aviso-error" role="alert">{error}</p>}
      {!error && !v && <p className="rotulo">Cargando</p>}
      {v && (
        <>
          {/* La cabecera del 2a, en este orden: veredicto y cifra en la MISMA fila —píldora
              compacta a la izquierda, cifra suelta a la derecha—, barra, el OBJETO del proceso, su
              entidad y referencia, y al final la prosa.

              La cifra va SUELTA y no dentro de la píldora: dentro obligaba a estirar la píldora a
              todo el ancho para que el número se leyera, y una píldora de 370 px deja de ser una
              etiqueta. Fuera, la píldora vuelve a medir lo que dice y el número puede ser grande. */}
          <div className="panel-cabeza">
            {/* Cerrar el acordeón. Solo existe en angosto: el documento pide que cerrarlo devuelva
                el scroll a donde estaba, y el navegador ya lo hace solo — al quitar el panel del
                flujo, la fila recupera su sitio. Por eso no hay que guardar `scrollY` a mano. */}
            {onCerrar && (
              <button type="button" className="panel-cerrar" onClick={onCerrar}
                      aria-label="Cerrar la decisión">
                <IconoQuitar />
              </button>
            )}
            {/* Sin perfil no hay veredicto: se dice, no se pinta un «80 Compite» en verde sobre
                un proceso que nadie ha comparado con lo que hace la empresa. */}
            {v.banda && v.afinidad !== null ? (
              <>
                <div className="panel-veredicto">
                  <Banda banda={v.banda} />
                  <span className="panel-cifra">
                    <b className="cifra">{v.afinidad}</b>
                    <span>/100 de afinidad</span>
                  </span>
                </div>
                <BarraAfinidad afinidad={v.afinidad} banda={v.banda} />
              </>
            ) : (
              <span className="sin-veredicto">Todavía sin afinidad</span>
            )}
            {proceso && (
              <div className="panel-quien">
                <p className="panel-objeto" title={proceso.descripcion || proceso.objeto}>
                  {normalizarObjeto(proceso.descripcion || proceso.objeto)}
                </p>
                <p className="panel-ref">
                  {[normalizarNombre(entidad?.nombre) || proceso.entidad_nit,
                    proceso.referencia || null].filter(Boolean).join(' · ')}
                </p>
              </div>
            )}
            <p className="panel-prosa">{v.prosa}</p>
          </div>

          <div className="panel-cuerpo">
          {v.criterios.length > 0 && (
            <section className="panel-bloque">
              <h3 className="rotulo">¿Se ajusta a tu empresa?</h3>
              <ul className="criterios">
                {v.criterios.map((c, i) => (
                  <li key={i} className={`criterio criterio-${c.signo}`}>
                    <Icono signo={c.signo} />
                    <span>{c.texto}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {v.claves.length > 0 && (
            <section className="panel-bloque">
              <h3 className="rotulo">Lo que decide</h3>
              {v.claves.map((k) => (
                <div key={k.clave} className="clave">
                  <div className="clave-linea">
                    <span>{k.clave}</span>
                    <b className="cifra">{k.valor}</b>
                  </div>
                  <p className="clave-nota">{k.nota}</p>
                  {/* Toda cifra promediada declara de cuántos casos sale: un promedio sin su n no
                      se puede pesar, y aquí se decide si invertir cuarenta horas. */}
                  <p className="clave-fuente">{k.fuente}</p>
                </div>
              ))}
            </section>
          )}

          <section className="panel-bloque">
            <h3 className="rotulo">Antes de decidir</h3>
            <p className="clave-nota">
              La afinidad compara este proceso con lo que tu empresa ya ejecutó y con tu RUP. No
              estima quién va a ganar.
            </p>
          </section>

          </div>

          {pidiendoMotivo ? (
            <div className="motivos">
              <span className="rotulo">¿Por qué lo descartas?</span>
              {MOTIVOS_DESCARTE.map((m) => (
                <button key={m} type="button" className="motivo"
                        onClick={() => { onMarcar(procesoId, 'desc', m); setPidiendoMotivo(false) }}>
                  {m}
                </button>
              ))}
            </div>
          ) : (
            <div className="panel-acciones">
              <button type="button" className="boton-primario"
                      onClick={() => navegar(`/proceso/${procesoId}`, { state: sitio })}>
                Abrir la ficha completa
              </button>
              <button type="button" className={`accion${marca === 'sigo' ? ' puesta' : ''}`}
                      aria-pressed={marca === 'sigo'} onClick={() => alternar('sigo')}>
                {marca === 'sigo' ? 'Siguiendo' : 'Seguir'}
              </button>
              <button type="button" className={`accion${marca === 'desc' ? ' puesta' : ''}`}
                      aria-pressed={marca === 'desc'} onClick={() => alternar('desc')}>
                {marca === 'desc' ? 'Descartado' : 'Descartar'}
              </button>
              {procesoId && (
                <QuePaso actual={resultado} onElegir={(r) => onResultado(procesoId, r)} />
              )}
            </div>
          )}
        </>
      )}
      </div>
    </aside>
  )
}

/** El icono acompaña; el significado lo lleva SIEMPRE el texto del criterio. */
function Icono({ signo }: { signo: Criterio['signo'] }) {
  if (signo === 'ok') return <IconoSi />
  if (signo === 'no') return <IconoNo />
  if (signo === 'alerta') return <IconoAviso />
  return <IconoSinDato />
}
